import { useCurrencyExchangeFormat } from "$routes/_lib/use-currency-exchange-format";
import useCurrencyFormatter from "$routes/patient/[:patientId]/_lib/invoices/hooks/useCurrencyFormatter";
import { printV2Receipt } from "$routes/patient/[:patientId]/_lib/printing/print-v2-receipt";
import useThermalPrinter from "$routes/patient/[:patientId]/_lib/printing/use-thermal-printer";
import { useState } from "react";
import { useIntl } from "react-intl";
import useFetchPrintableInvoice from "./useFetchPrintableInvoice";

function patchCurrencyFormatter(
  currencyFormatter: (amountInPence: number) => string,
): {
  format: (amountInPence: number) => string;
} {
  // transcodes UTF-8 currency symbols to be compatible with thermal printers
  return {
    format: (amountInPence) =>
      currencyFormatter(amountInPence).replace(/₵/g, "¢"),
  };
}

export default function usePrintThermalReceipt(invoiceId: string) {
  const intl = useIntl();
  const { currencyFormatter } = useCurrencyFormatter();
  const currencyExchangeFormat = useCurrencyExchangeFormat();
  const {
    print,
    havePrinter,
    reset: resetPrinter,
    error: printerError,
  } = useThermalPrinter();
  const [fetchError, setFetchError] = useState<Error>(null);

  // we use async refetch so we can fetch the full invoice before printing without messing with more state hooks...
  const { refetch: fetchInvoice } = useFetchPrintableInvoice(invoiceId, {
    fetchPolicy: "network-only",
    skip: true,
  });

  const resetErrors = () => {
    resetPrinter();
    setFetchError(null);
  };

  async function thermalPrintReceipt(invoice, printer) {
    // FIXME shimmed to match invoice V2 API; refactor after V2.1 launch...
    // (our currencyFormatter already converts pence to pounds)
    await printV2Receipt({
      invoice,
      printer,
      currencyFormatter: patchCurrencyFormatter(currencyFormatter),
      penceToPounds: (i) => i,
      intl,
      currencyExchangeFormat,
    });
  }

  const printReceipt = async () => {
    try {
      const {
        data: { invoice },
        error: err,
      } = await fetchInvoice();
      if (err) {
        // NB because we are using Apollo refetch, an error may be thrown instead of returned
        // https://github.com/apollographql/apollo-link-rest/issues/262 et al
        setFetchError(err);
      } else {
        await print((printer) => thermalPrintReceipt(invoice, printer));
      }
    } catch (e) {
      setFetchError(e);
    }
  };

  return { havePrinter, printReceipt, resetErrors, fetchError, printerError };
}
